/* =====================================================================
   WavvyBaby — Meta Pixel wrapper + dedup-aware event helpers.
   Bootstrapped by `snippets/wavvy-meta-pixel.liquid`, which sets:
     window.wavvyMeta = { pixelId, advancedMatching, testEventCode,
                          capiEndpoint, customer: {email,fn,ln,id} }

   We thin-wrap `fbq` rather than reimplementing it:
   - bootMetaPixel() runs fbq('init', pixelId[, hashed userData]) + PageView
   - metaTrack(eventName, params, opts?) generates an eventID, fires fbq,
     and (optionally) forwards a copy to our CAPI Worker so the same
     eventID is sent server-side. Meta dedups on (event_name, event_id).
   - getFbCookies() reads _fbc/_fbp; getEventId() returns a UUID.
   - persistPurchaseEventId() writes the eventID into the Shopify cart
     attributes so the orders/paid webhook can read it back.
   ===================================================================== */
(function () {
  'use strict';

  // ---------- crypto: SHA-256 hex via Web Crypto, no deps ----------
  function sha256Hex(value) {
    if (!value) return Promise.resolve('');
    var clean = String(value).trim().toLowerCase();
    var bytes = new TextEncoder().encode(clean);
    return crypto.subtle.digest('SHA-256', bytes).then(function (buf) {
      return Array.from(new Uint8Array(buf))
        .map(function (b) { return b.toString(16).padStart(2, '0'); })
        .join('');
    });
  }

  // ---------- cookies + ids ----------
  function readCookie(name) {
    var match = document.cookie.match(new RegExp('(?:^|; )' + name + '=([^;]*)'));
    return match ? decodeURIComponent(match[1]) : null;
  }
  function getFbCookies() {
    return { fbc: readCookie('_fbc'), fbp: readCookie('_fbp') };
  }
  function getEventId() {
    if (window.crypto && crypto.randomUUID) return crypto.randomUUID();
    // RFC4122-ish fallback for older browsers
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
      var r = (Math.random() * 16) | 0;
      var v = c === 'x' ? r : (r & 0x3) | 0x8;
      return v.toString(16);
    });
  }

  // ---------- init ----------
  function bootMetaPixel() {
    var cfg = window.wavvyMeta;
    if (!cfg || !cfg.pixelId || typeof fbq !== 'function') return;

    if (!cfg.advancedMatching || !cfg.customer || !cfg.customer.email) {
      fbq('init', cfg.pixelId);
      fbq('track', 'PageView');
      return;
    }

    // Advanced Matching — hash PII before re-init
    var c = cfg.customer;
    Promise.all([
      sha256Hex(c.email),
      sha256Hex(c.fn),
      sha256Hex(c.ln),
      // external_id is NOT email-hashed by spec; pass customer id as-is or
      // hashed depending on Meta's requirements (hashed is safer).
      sha256Hex(c.id)
    ]).then(function (hashed) {
      var userData = {};
      if (hashed[0]) userData.em = hashed[0];
      if (hashed[1]) userData.fn = hashed[1];
      if (hashed[2]) userData.ln = hashed[2];
      if (hashed[3]) userData.external_id = hashed[3];
      fbq('init', cfg.pixelId, userData);
      fbq('track', 'PageView');
    }).catch(function () {
      fbq('init', cfg.pixelId);
      fbq('track', 'PageView');
    });
  }

  // ---------- track ----------
  // metaTrack(name, params, { eventId?, sendToCapi? })
  function metaTrack(name, params, opts) {
    var cfg = window.wavvyMeta || {};
    if (!cfg.pixelId || typeof fbq !== 'function') return null;
    opts = opts || {};
    var eventId = opts.eventId || getEventId();

    var trackParams = Object.assign({}, params || {});
    var trackOpts = { eventID: eventId };
    if (cfg.testEventCode) trackOpts.test_event_code = cfg.testEventCode;

    try {
      fbq('track', name, trackParams, trackOpts);
    } catch (e) {
      // Pixel can throw if the ID is wrong — never let it break the page.
      if (window.console) console.warn('metaTrack failed', e);
    }

    // Forward to CAPI Worker (browser-originated CAPI ping). The Worker
    // builds the user_data envelope from cookies + IP + UA on its side and
    // emits the same eventID so Meta dedupes.
    if (cfg.capiEndpoint && opts.sendToCapi !== false && name !== 'Purchase') {
      var fbCookies = getFbCookies();
      var payload = {
        event_name: name,
        event_id: eventId,
        event_source_url: window.location.href,
        action_source: 'website',
        custom_data: trackParams,
        user_data: {
          fbc: fbCookies.fbc || null,
          fbp: fbCookies.fbp || null,
          client_user_agent: navigator.userAgent
        },
        test_event_code: cfg.testEventCode || null
      };
      try {
        fetch(cfg.capiEndpoint + '/event', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
          keepalive: true
        }).catch(function () { /* swallow — pixel already fired */ });
      } catch (e) { /* noop */ }
    }

    return eventId;
  }

  // ---------- purchase event_id threading via Shopify cart attributes ----------
  function persistPurchaseEventId(eventId) {
    if (!eventId) return Promise.resolve();
    var fb = getFbCookies();
    var attrs = {
      attributes: {
        fb_purchase_event_id: eventId,
        fb_fbp: fb.fbp || '',
        fb_fbc: fb.fbc || '',
        fb_event_source_url: window.location.href
      }
    };
    return fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(attrs),
      keepalive: true
    }).catch(function () { /* swallow */ });
  }

  // ---------- expose ----------
  window.wavvyMeta = window.wavvyMeta || {};
  window.wavvyMeta.boot = bootMetaPixel;
  window.wavvyMeta.track = metaTrack;
  window.wavvyMeta.persistPurchaseEventId = persistPurchaseEventId;
  window.wavvyMeta.getFbCookies = getFbCookies;
  window.wavvyMeta.getEventId = getEventId;

  // ---------- global checkout listener ----------
  // Catches every form submit / button click that triggers Shopify checkout
  // and fires InitiateCheckout with a fresh event_id, then persists the id
  // into cart attributes so the orders/paid webhook can match it on the
  // server side. Async-safe — uses preventDefault → await persist → resubmit.
  function wireCheckoutListener() {
    document.addEventListener('click', function (e) {
      var btn = e.target && (e.target.closest('button[name="checkout"]') || e.target.closest('[data-meta-checkout]'));
      if (!btn) return;
      var form = btn.form || btn.closest('form');
      if (!form) return;
      // Only intercept forms that POST to the cart route (real Shopify checkout submit)
      var action = (form.getAttribute('action') || '').toLowerCase();
      if (!action.includes('/cart')) return;

      if (form.dataset.metaIntercepted === '1') return;  // already running
      form.dataset.metaIntercepted = '1';

      e.preventDefault();
      var eventId = getEventId();
      var lineItems = [];
      var totalValue = 0;
      var currency = window.Shopify && Shopify.currency ? Shopify.currency.active : 'USD';

      var fireAndContinue = function () {
        metaTrack('InitiateCheckout', {
          content_ids: lineItems.map(function (i) { return String(i.id); }),
          contents: lineItems,
          num_items: lineItems.reduce(function (n, i) { return n + (i.quantity || 1); }, 0),
          currency: currency,
          value: totalValue
        }, { eventId: eventId });

        persistPurchaseEventId(eventId).finally(function () {
          // Restore the button and resubmit
          btn.disabled = false;
          form.submit();
        });
      };

      btn.disabled = true;

      // Pull live cart for accurate value + line items
      fetch('/cart.js', { headers: { 'Accept': 'application/json' } })
        .then(function (r) { return r.json(); })
        .then(function (cart) {
          if (cart.currency) currency = cart.currency;
          totalValue = (cart.total_price || 0) / 100;
          lineItems = (cart.items || []).map(function (it) {
            return {
              id: it.product_id ? String(it.product_id) : String(it.id),
              quantity: it.quantity || 1,
              item_price: (it.price || 0) / 100
            };
          });
        })
        .catch(function () { /* fall through with empty cart */ })
        .finally(fireAndContinue);
    }, true);
  }

  // ---------- newsletter signup listener (footer email form) ----------
  function wireSubscribeListener() {
    document.addEventListener('submit', function (e) {
      var form = e.target;
      if (!form || !form.classList) return;
      // Match the footer email form OR any form with the Shopify newsletter contact[tags] hidden input
      var isNewsletter = form.classList.contains('wv-footer__email-form') ||
        !!form.querySelector('input[name="contact[tags]"][value="newsletter"]');
      if (!isNewsletter) return;
      var emailInput = form.querySelector('input[type="email"], input[name="contact[email]"]');
      var email = emailInput ? emailInput.value : '';
      metaTrack('Subscribe', { content_name: 'Newsletter', currency: 'USD', value: 0, email: email });
    }, true);
  }

  // Auto-boot on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      bootMetaPixel();
      wireCheckoutListener();
      wireSubscribeListener();
    }, { once: true });
  } else {
    bootMetaPixel();
    wireCheckoutListener();
    wireSubscribeListener();
  }
})();
